import React from 'react';
import { useParams } from 'react-router-dom';
import { useDashboard } from '../hooks/useDashboard';
import { ClientOverview, ClientVoiceQuote, GeneralInsights, TimelineItem, ProjectStatus } from '../types';
import AISummary from './AISummary';
import ClientOverviewSection from './ClientOverviewSection';
import ClientVoiceSection from './ClientVoiceSection';
import ErrorMessage from './ErrorMessage';
import GeneralInsightsSection from './GeneralInsightsSection';
import LoadingSpinner from './LoadingSpinner';
import ProjectStatusSection from './ProjectStatusSection';
import QuestionsSection from './QuestionsSection';
import WhatsNextSection from './WhatsNextSection';

const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const {
    data,
    loading,
    error,
    refetch,
    updateClientOverview,
    updateGeneralInsights,
    updateClientVoice,
    updateTimeline,
    updateProjectStatus,
    updateAISummary
  } = useDashboard(clientId);

  const handleClientOverviewUpdate = (updatedOverview: ClientOverview) => {
    updateClientOverview(updatedOverview);
  };

  const handleClientVoiceUpdate = async (updatedVoices: ClientVoiceQuote[]) => {
    await updateClientVoice(updatedVoices);
  };

  const handleGeneralInsightsUpdate = async (updatedInsights: GeneralInsights) => {
    await updateGeneralInsights(updatedInsights);
  };

  const handleTimelineUpdate = async (updatedTimeline: TimelineItem[]) => {
    await updateTimeline(updatedTimeline);
  };

  const handleProjectStatusUpdate = async (updatedProjectStatus: ProjectStatus) => {
    await updateProjectStatus(updatedProjectStatus);
  };

  // Show loading spinner while dashboard data is loading
  if (loading) {
    return <LoadingSpinner />;
  }

  // Show error message if dashboard data failed to load or no clientId
  if (error || !data || !clientId) {
    return <ErrorMessage message={error || 'Failed to load dashboard data'} onRetry={refetch} />;
  }

  return (
    <div className="px-10 py-5 max-w-7xl mx-auto">
      {/* Client Header */}
      <div className="mb-8 flex">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 mb-auto mt-2 rounded-full bg-gray-100 flex items-center justify-center p-1 overflow-hidden">
            {data.clientOverview.client.logo ? (
              <img
                src={data.clientOverview.client.logo}
                alt={`${data.clientOverview.client.name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to initials if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm"
              style={{ display: data.clientOverview.client.logo ? 'none' : 'flex' }}
            >
              {data.clientOverview.client.name.charAt(0).toUpperCase()}
            </div>
          </div>
          <div>
            <div className="flex items-center">
              <h1 className="text-[48px] font-satoshi font-bold text-gray-900 m-0 leading-tight">
                {data.clientOverview.client.name}
              </h1>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Last updated: {data.clientOverview.client.lastUpdated || 'Unknown'}
            </div>
          </div>
        </div>
      </div>

      <AISummary summary={data.aiSummary} clientId={clientId} onUpdate={updateAISummary} />

      <ClientOverviewSection
        clientOverview={data.clientOverview}
        onUpdate={handleClientOverviewUpdate}
      />
      <ProjectStatusSection
        projectStatus={data.projectStatus}
        ultimateGoal={data.clientOverview.ultimateGoal}
        clientId={clientId}
        onUpdate={handleProjectStatusUpdate}
      />
      <GeneralInsightsSection
        generalInsights={data.generalInsights}
        clientId={clientId}
        onUpdate={handleGeneralInsightsUpdate}
      />
      <ClientVoiceSection
        clientVoice={data.clientVoice}
        clientId={clientId}
        onUpdate={handleClientVoiceUpdate}
      />
      <WhatsNextSection
        timeline={data.timeline}
        clientId={clientId}
        onUpdate={handleTimelineUpdate}
      />
      <QuestionsSection
        contacts={data.clientOverview.contacts}
        clientName={data.clientOverview.client.name}
        clientId={clientId}
      />
    </div>
  );
};

export default ClientDetailPage; 